import {
  ApiBankDetails,
  ApiBaseWorkerService,
  ApiDeleteFiles,
  ApiGoogleCalendarItem,
  ApiOrderListFilteredItem,
  ApiOrderNote, ApiWorker,
  ApiWorkerCommonFilters,
  ApiWorkerData,
  ApiWorkerDetails,
  ApiWorkerFilters,
  ApiWorkerInvoice,
  ApiWorkerOrderFilter,
  ApiWorkerOrderOffer,
  ApiWorkerPostCodeResponse,
  ApiWorkerSendMessage,
  ApiWorkerService } from "./interface.api";
import { ApiOrder, ApiOrderFile } from "../order/interface.api";
import { NewWorkerParams, WorkerApi } from "./interface";
import { Order, Worker, WorkerOrderOffer } from "@/types";
import { WorkerCommonFilters, WorkerFilters } from "./interface";
import { getGooglePath, request, sendFiles } from "../request";
import { mapApiOrder, mapApiOrderFiles } from "../order/mappers";
import { mapApiOrderNotes, workerApiMapper, workerDetailsApiMapper, workerInvoicesApiMapper, workerOrderFilterMapper, workerOrderOffersApiMapper, workerServicesApiMapper, workersApiMapper } from './mappers';
import { useCallback, useState } from "react";

import { ApiHooksProps } from "../interface";
import { OrderFilterParams } from "../order/interface";

const hook = ({ setError, setLoading, setSuccess, serverDownloadPath, serverPath, token }: ApiHooksProps): WorkerApi => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [workerOrders, setWorkerOrders] = useState<Order[]>([]);
  const [workerOrderOffers, setWorkerOrderOffers] = useState<WorkerOrderOffer[]>([]);
  
  const acceptOffer =  useCallback(async (id: string) => {
    const path = `${serverPath}/workers/confirm-order/${id}`;
    request(path, { token });
  }, [serverPath, token]);
  
  const addNote = useCallback(async (workerId: number, note: string) => {
    const path = `${serverPath}/notes/create`;
    const body = {
      Content: note,
      WorkerId: workerId
    }

    await request(path, { body, token });
  }, [serverPath, token]);

  const addPostcode = useCallback(async (workerId: number, postcode: string) => {
    const body = {
      Postcode: postcode,
      WorkerId: workerId,
    };
    const path = `${serverPath}/workers/add-postcode/`;
    const res = await request<typeof body, ApiWorkerPostCodeResponse>(path, { body, token });
    setSuccess('Postcode added');

    return res;
  }, [serverPath, token]);

  const combinePhotos = useCallback(async (orderServiceId: string, fileIds: string[], isOffered: boolean) => {
    const path = `${serverPath}/workers/combine-files/${orderServiceId}`;
    const body = {
      IsOffered: isOffered,
      files: fileIds.map(f => ({ UploadFileId: f }))
    };

    await request(path, { body, token });
  }, [serverPath, token]);

  const create = useCallback(async (worker: NewWorkerParams) => {
    const path = `${serverPath}/workers/new`;
    const body = {
      ...worker
    }
    const res = await request<NewWorkerParams, ApiWorker>(path, { body, token });
    const newWorker = workerApiMapper(res);
    setWorkers([...workers, newWorker]);
  }, [serverPath, token, workers]);
  
  const deletePhotos = useCallback(async (uploadFileIds: string[], orderServiceId: string, isOffered: boolean)=> {
    const path = `${serverPath}/workers/delete-files/${orderServiceId}`;

    const body: ApiDeleteFiles = {
      IsOffered : isOffered,
      files: uploadFileIds.map((uploadFileId) => ({
        UploadFileId: uploadFileId,
      }))
    };

    return await request<ApiDeleteFiles, void>(path, { body, token });
  }, [serverPath, token]);
  
  const deletePortfolioImage = useCallback( async (uploadFileId: string)=> {
    const path = `${serverPath}/workers/delete-portfolio-item/${uploadFileId}`;
    setLoading(true);
    await request(path, { token });
    setLoading(false);
    setSuccess('Portfolio image deleted');
  }, [serverPath, token]);

  const deletePostcode = useCallback( async (postcodeId: string) => {
    const path = `${serverPath}/workers/delete-postcode/`;

    const body = {
      WorkerPostCodeId: postcodeId
    }

    setLoading(true);
    await request(path, { body, token });
    setLoading(false);
    setSuccess('Postcode deleted');
  }, [serverPath, token]);

  const deleteWorker = useCallback( async (workerId: string) => {
    const path = `${serverPath}/workers/delete/${workerId}`;

    setLoading(true);
    await request(path, { method: 'DELETE', token });
    setLoading(false);
    setSuccess('Worker deleted');
  }, [serverPath, token]);

  const getCalendarId = useCallback( async (workerId: string) => {
    const path = `${serverPath}/workers/get-calendar/${workerId}`;
    
    return await request<never, string>(path, { token });
  }, [serverPath, token]);
  
  const getCalendarList = useCallback( async () => {
    const path = `${serverPath}/workers/get-google-calendars`;
    
    try {
    return await request<never, ApiGoogleCalendarItem[]>(path, { method: 'GET', token });
    }
    catch {
      return [];
    }
  }, [serverPath, token]);

  const getData = async (workerId: string) => {
    const path = `${serverPath}/workers/get-data/${workerId}/`;
    setLoading(true);
    const res = await request<never, ApiWorkerData>(path, { token });
    setLoading(false);

    return workerDetailsApiMapper(res);
  };

  const getFilteredList = useCallback(async (filters?: WorkerFilters) => {
    const body: ApiWorkerFilters = {
      DisplayName: filters?.displayName || '',
      EmailAddress: filters?.email || '',
      Login: filters?.login || '',
    };
    const path = `${serverPath}/workers/filtered-list`;
    const res = await request<ApiWorkerFilters, ApiWorker[]>(path, { body, token});
    setWorkers(workersApiMapper(res))
  }, [serverPath, token]);
  
  const getFloorArea = useCallback(async (orderServiceId: string)=> {
    const path = `${serverPath}/workers/get-floor-area/${orderServiceId}`;
    const res = await request<never, { FloorArea: number }>(path, { method: 'GET', token});

    return res?.FloorArea ?? 0;
  }, [serverPath, token]);
  
  const getVirtualTour = useCallback(async (orderServiceId: string)=> {
    const path = `${serverPath}/workers/get-virtual-tour/${orderServiceId}`;
    const res = await request<never, { VirtualTourPhotos: number }>(path, { method: 'GET', token});

    return res?.VirtualTourPhotos ?? 0;
  }, [serverPath, token]);

  const getList = useCallback(async (filters?: WorkerCommonFilters) => {
    const body: ApiWorkerCommonFilters = {
      EmailAddress: filters?.email || '',
      IsActive: typeof(filters?.active) == "undefined" ? null : filters?.active,
      Name : filters?.name || '',
      PostCode: filters?.postCode || '',
      WorkerTypeId: filters?.workerTypeId || null,
    };
    const path = `${serverPath}/workers/list`;
    setLoading(true);
    const res = await request<ApiWorkerCommonFilters, ApiWorker[]>(path, { body, token});
    setWorkers(workersApiMapper(res))
    setLoading(false);
  }, [serverPath, token]);
  
  const getNotes = useCallback(async (workerId: string) => {
    const path = `${serverPath}/workers/${workerId}/notes`;

    const res = await request<never, ApiOrderNote[]>(path, { method: 'GET', token });

    return mapApiOrderNotes(res);
  }, [serverPath, token]);
  
  const getOrderList = useCallback(async (data?: OrderFilterParams) => {
    const path = `${serverPath}/workers/order-list/`;

    setLoading(true);
    const body = workerOrderFilterMapper(data)
    const res = await request<ApiWorkerOrderFilter, ApiOrder[]>(path, { body, method: 'POST', token });

    setLoading(false);

    return setWorkerOrders(mapApiOrder(res));
  }, [serverPath, token]);
  
  const getOrderOfferList = useCallback(async (id?: string) => {
    const path = `${serverPath}/workers/get-order-offers/${id || 'own'}`;
    const res = await request<never, ApiWorkerOrderOffer[]>(path, { method: 'GET', token });

    return setWorkerOrderOffers(workerOrderOffersApiMapper(res));
  }, [serverPath, token]);
  
  const getOrderOfferCount = useCallback(async () => {
    const path = `${serverPath}/workers/order-offers-count`;

    return await request<never, number>(path, { method: 'GET', token });
  }, [serverPath, token]);
  
  const getPortfolio = useCallback(async (id: string)=> {
    const path = `${serverPath}/workers/get-portfolio/${id}`;
    request(path, { token });

    const res = await request<never, ApiOrderFile[]>(path, { token });

    return mapApiOrderFiles(res);
  }, [serverPath, token]);

  const getServices = async (workerId: string) => {
    const path = `${serverPath}/workers/get-services/${workerId}`;
    const res = await request<never, ApiWorkerService[]>(path, { token });

    return workerServicesApiMapper(res);
  };

  const getWorkerOfferList =  useCallback(async () => {
    const path = `${serverPath}/workers/get-confirm-notifications/`;
    const res = await request<never, ApiOrderListFilteredItem[]>(path, { token });

    return res;
  }, [serverPath, token]);

  const getWorkerInvoiceList = useCallback(async(filterType: string) => {
    const path = `${serverPath}/workers/list-invoices/${filterType}`;
    const res = await request<never, ApiWorkerInvoice[]>(path, { token });

    return workerInvoicesApiMapper(res);
  }, [serverPath, token]);

  const inviteWorker = useCallback(async (workerId: string) => {
    const path = `${serverPath}/workers/invite/${workerId}`;
    await request(path, { token });
  }, [serverPath, token]);

  const movePhotos = useCallback(async (orderServiceId: string, fileIds: string[], isOffered: boolean) => {
    const path = `${serverPath}/workers/move-files/${orderServiceId}`;
    const body = {
      IsOffered: isOffered,
      files: fileIds.map(f => ({ UploadFileId: f }))
    };

    await request(path, { body, token });
  }, [serverPath, token]);
  
  const recordFloorArea = useCallback(async (orderServiceId: string, floorArea: number) => {
    const path = `${serverPath}/workers/record-floor-area/${orderServiceId}`;
    const body = {
      FloorArea: floorArea
    }

    await request(path, { body, token });
  }, [serverPath, token]);
  
  const recordVirtualTour = useCallback(async (orderServiceId: string, virtualTour: number) => {
    const path = `${serverPath}/workers/record-virtual-tour/${orderServiceId}`;
    const body = {
      VirtualTourPhotos: virtualTour
    }

    await request(path, { body, token });
  }, [serverPath, token]);
  
  const redirectSyncGoogleAccount = useCallback(async () => {
    const path = `${getGooglePath()}?redirect=${encodeURIComponent(window.location.href)}`;

    await request<never, string>(path, { method: 'GET', token }).then((res) => {
        window.location.href = res;
    });
  }, [serverPath, token]);

  const rejectOffer = useCallback(async (id: string, reason: string) => {
    const path = `${serverPath}/workers/reject-order/${id}`;
    const body = { Reason: reason };
    await request(path, { body, token });
  }, [serverPath, token]);

  const sendOrderMessage = useCallback(async (body: ApiWorkerSendMessage) => {
    const path = `${serverPath}/workers/order-message/`;
    await request(path, { body, token });
  }, [serverPath, token]);

  const setInvoiceApproved = useCallback(async (ids: string[]) => {
    const path = `${serverPath}/workers/set-invoice-approved/`;

    await request(path, { body: ids, token });
    setSuccess('Invoice(s) approved');
  }, [serverPath, token]);

  const setInvoicePaid = useCallback(async (ids: string[]) => {
    const path = `${serverPath}/workers/set-invoice-paid/`;

    await request(path, { body: ids, token });
    setSuccess('Invoice(s) set to paid');
  }, [serverPath, token]);

  const deleteInvoice = useCallback(async (id: string) => {
    const path = `${serverPath}/workers/delete-invoice/${id}`;
    await request(path, { token });
    setSuccess('Invoice deleted');
  }, [serverPath, token]);
  
  const unlinkGoogleCalendar = useCallback(async () => {
    const path = `${serverPath}/workers/unbind-google-calendar`;
    
    return await request<never, boolean>(path, { token });
  }, [serverPath, token]);
  
  const updateBankDetails = useCallback(async (userId: string, data: ApiBankDetails ) => {
    const path = `${serverPath}/workers/update-bank-details/${userId}`;
    const body = data;

    await request(path, { body, token });
    setSuccess('Bank details updated');
  }, [serverPath, token]);

  const updateDetails = useCallback(async (userId: string, data: ApiWorkerDetails ) => {
    const path = `${serverPath}/workers/update-details/${userId}`;
    const body = data;
    setLoading(true);
    await request(path, { body, token });
    setLoading(false);
    setSuccess('Details updated');
  }, [serverPath, token]);

  const updateServices = useCallback(async (userId: string, data: ApiBaseWorkerService[]) => {
    const path = `${serverPath}/workers/update-services/${userId}`;
    const body = {
      Services: data
    };
    setLoading(true);

    request(path, { body, token });
    setLoading(false);
    setSuccess('Services updated');
  }, [serverPath, token]);

  const uploadEditedPhoto = useCallback((files: Blob[], uploadFileId: string, isDraft: boolean) => {
    const path = `${serverPath}/workers/upload-edited${isDraft ? "-draft" : ""}-file/${uploadFileId}`;
    
    return sendFiles<void>(path, files, token);
  }, [serverPath, token]);

  const uploadFileOffered = useCallback((files: Blob[], offeredServiceId: string) => {
    const path = `${serverPath}/workers/upload-file-offered/${offeredServiceId}`;
    
    return sendFiles<void>(path, files, token);
  }, [serverPath, token]);

  const uploadPhotos = useCallback(async (files: Blob[], orderServiceId: string, isDraft: boolean) => {
    const path = `${serverDownloadPath}/workers/upload${isDraft ? "-draft" : ""}-file/${orderServiceId}`;
    setLoading(true);
    
    try {
        const res = await sendFiles<void>(path, files, token);
        setLoading(false);
        setSuccess('Photos uploaded');

        return res;
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }

    return Promise.reject();
  }, [serverPath, token]);

  const uploadPlans = useCallback(async (files: Blob[], orderServiceId: string, isDraft : boolean) => {
    const path = `${serverDownloadPath}/workers/upload${isDraft ? "-draft" : ""}-plan/${orderServiceId}`;
    setLoading(true);
    try
    {
    const res = await sendFiles<void>(path, files, token);

    setLoading(false);
    setSuccess('Plans uploaded');

    return res;
    } catch (error: unknown) {
     if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }

    return Promise.reject();
  }, [serverPath, token]);

  const uploadPortfolio = useCallback(async (files: Blob[]) => {
    const path = `${serverDownloadPath}/workers/upload-portfolio/`;

    const res = await sendFiles<ApiOrderFile[]>(path, files, token);

    return mapApiOrderFiles(res);
  }, [serverPath, token]);

  return {
    acceptOffer,
    addNote,
    addPostcode,
    combinePhotos,
    create,
    deleteInvoice,
    deletePhotos,
    deletePortfolioImage,
    deletePostcode,
    deleteWorker,
    getCalendarId,
    getCalendarList,
    getData,
    getFilteredList,
    getFloorArea,
    getList,
    getNotes,
    getOrderList,
    getOrderOfferCount,
    getOrderOfferList,
    getPortfolio,
    getServices,
    getVirtualTour,
    getWorkerInvoiceList,
    getWorkerOfferList,
    inviteWorker,
    movePhotos,
    recordFloorArea,
    recordVirtualTour,
    redirectSyncGoogleAccount,
    rejectOffer,
    sendOrderMessage,
    setInvoiceApproved,
    setInvoicePaid,
    unlinkGoogleCalendar,
    updateBankDetails,
    updateDetails,
    updateServices,
    uploadEditedPhoto,
    uploadFileOffered,
    uploadPhotos,
    uploadPlans,
    uploadPortfolio,
    workerOrderOffers,
    workerOrders,
    workers
  }
};

export default hook;
